import {
  Component,
  OnInit,
  ChangeDetectorRef,
  AfterViewChecked,
  OnDestroy,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { Credentials } from '../../../shared/objects/auth/credentials';
import { MfaChallenge, MfaParams } from '../../../shared/objects/auth/mfa';
import { LoginStatusEnum } from '../../../shared/objects/enum/loginStatus';
import { OuterParameter } from '../../../shared/objects/auth/outerParam';
import { UserService } from '../../secure/admin/manage-users/shared/user.service';
import { BrandingService } from '../../../core/services/branding.service';
import { GlobalResponsivenessService } from '../../../core/services/global-responsiveness.service';
import { Subscription } from 'rxjs';
import { NotificationService } from '../../../core/services/notification.service';
import { NotificationType } from '../../../shared/enums/notification-type';
import { FiltersService } from '../../../core/services/filters.service';

@Component({
    selector: 'app-root',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, AfterViewChecked, OnDestroy {
  isCollapsed: boolean;
  textTodisplay: string;
  outerParam: OuterParameter;

  username: string;
  password: string;
  loginStatus: LoginStatusEnum = LoginStatusEnum.NONE;
  NotificationType = NotificationType;

  Status = LoginStatusEnum;
  subscriptions: Subscription[] = [];

  /** Set once the password was accepted and Keycloak asks for the second factor. */
  mfaChallenge: MfaChallenge = null;
  private readonly mfaStatuses = [
    LoginStatusEnum.MFA_REQUIRED,
    LoginStatusEnum.MFA_SETUP_REQUIRED,
    LoginStatusEnum.INVALID_MFA_CODE,
    LoginStatusEnum.INVALID_RECOVERY_CODE,
  ];

  private knownActions: string[] = [];

  public readonly ACTION_DENY = 'deny';
  public readonly ACTION_CONFIRM = 'confirm';
  public readonly ACTION_BID = 'bid';
  public readonly ACTION_BID_LIST = 'bid-list';
  public readonly ACTION_CARRIER_CERTIFICATE = 'carriers';
  public readonly ACTION_ORDER = 'order';
  public readonly ACTION_ORDER_LIST = 'order-list';

  get description(): string {
    return this.brandingService.getLoginBody();
  }

  get footer(): string {
    return this.brandingService.getLoginFooter();
  }

  get website(): string {
    return this.brandingService.getWebsite();
  }

  get title(): string {
    return this.brandingService.getLoginTitle();
  }

  get outerParameterIsValid(): boolean {
    return !!this.outerParam && this.actionIsKnown(this.outerParam.action);
  }

  get knowActionOuterParam() {
    return (
      !!this.outerParam &&
      (this.outerParam.action === this.ACTION_CONFIRM ||
        this.outerParam.action === this.ACTION_DENY ||
        this.outerParam.action === this.ACTION_BID ||
        this.outerParam.action === this.ACTION_BID_LIST ||
        this.outerParam.action === this.ACTION_CARRIER_CERTIFICATE ||
        this.outerParam.action === this.ACTION_ORDER ||
        this.outerParam.action === this.ACTION_ORDER_LIST)
    );
  }

  leftLoginAttempts = 0;
  carrierCertificateId = null;
  constructor(
    private router: Router,
    private authService: AuthService,
    private activatedRoute: ActivatedRoute,
    private userService: UserService,
    private brandingService: BrandingService,
    private cd: ChangeDetectorRef,
    private globalResponsive: GlobalResponsivenessService,
    private notificationService: NotificationService,
  ) {
    this.authService.logout();
    this.activatedRoute.params.subscribe(params => {
      this.registerKnownActions();
      const urlParam = new OuterParameter(params.guid, params.action);
      if (!!urlParam.action) {
        this.outerParam = urlParam;
      }
    });
    this.carrierCertificateId =
      this.activatedRoute.snapshot.queryParamMap.get('certificateId');
    var startResolution = this.globalResponsive.getCurrentResolution();
    if (startResolution == '1920x969' || startResolution == '1280x913') {
      this.isCollapsed = false;
    } else {
      this.isCollapsed = true;
    }
  }

  ngOnInit(): void {
    const localLanguage = localStorage.getItem('cp-language');
    const isNL = localStorage.getItem('cp-language') === 'nl';

    FiltersService.initFilters();
    this.globalResponsive.isDesktop$.subscribe(isDesktop => {
      if (
        this.globalResponsive.getCurrentResolution() != '1280x609' &&
        isDesktop
      ) {
        this.isCollapsed = false;
      } else {
        this.isCollapsed = true;
      }
      this.cd.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  login(mfa?: MfaParams) {
    const credentials: Credentials = new Credentials();
    credentials.username = this.username;
    credentials.password = this.password;
    this.loginStatus = LoginStatusEnum.WAITING;
    this.authService.login(credentials, mfa).then(res => {
      if (
        this.mfaChallenge &&
        !this.mfaStatuses.includes(res.status) &&
        res.status !== LoginStatusEnum.SUCCESS &&
        res.status !== LoginStatusEnum.ERROR
      ) {
        this.cancelMfa();
      }
      switch (res.status) {
        case LoginStatusEnum.MFA_REQUIRED:
        case LoginStatusEnum.MFA_SETUP_REQUIRED:
          this.mfaChallenge = res.mfaChallenge;
          this.loginStatus = res.status;
          this.cd.markForCheck();
          break;
        case LoginStatusEnum.INVALID_MFA_CODE:
        case LoginStatusEnum.INVALID_RECOVERY_CODE:
        case LoginStatusEnum.ERROR:
          this.loginStatus = res.status;
          this.cd.markForCheck();
          break;
        case LoginStatusEnum.SUCCESS:
          this.loginStatus = res.status;
          this.cd.markForCheck();
          this.redirectWithAccess();
          break;
        case LoginStatusEnum.INVALID_CREDENTIALS:
          this.leftLoginAttempts = 3 - res.attempts;
          this.loginStatus = res.status;
          this.cd.markForCheck();
          break;
        case LoginStatusEnum.PASSWORD_RESET:
          this.loginStatus = res.status;
          this.authService.logout();
          this.cd.markForCheck();
          break;
        case LoginStatusEnum.BLOCKED:
          this.loginStatus = res.status;
          this.cd.markForCheck();
          break;
        case LoginStatusEnum.BLOCKED_WITH_REDIRECT:
          this.loginStatus = res.status;
          this.redirectWithNoAccess();
          break;
        default:
          this.loginStatus = LoginStatusEnum.NONE;
          break;
      }
    });
  }

  /** Back from the second step to the username / password form. */
  cancelMfa(): void {
    this.mfaChallenge = null;
    this.password = '';
    this.loginStatus = LoginStatusEnum.NONE;
    this.cd.markForCheck();
  }

  private redirectWithAccess(): void {
    if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_CONFIRM
    ) {
      this.router.navigate([`secure/confirm/${this.outerParam.guid}`]);
    } else if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_DENY
    ) {
      this.router.navigate([`secure/reject/${this.outerParam.guid}`]);
    } else if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_BID
    ) {
      this.router.navigate([`secure/freight-exchange/${this.outerParam.guid}`]);
    } else if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_BID_LIST
    ) {
      this.router.navigate([`secure/freight-exchange`]);
    } else if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_CARRIER_CERTIFICATE
    ) {
      this.router.navigate([`secure/carriers/my-profile`], {
        queryParams: { certificateId: this.carrierCertificateId },
      });
    } else if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_ORDER
    ) {
      this.router.navigate([`secure/order-list/${this.outerParam.guid}`]);
    } else if (
      this.outerParameterIsValid &&
      this.outerParam.action === this.ACTION_ORDER_LIST
    ) {
      this.router.navigate([`secure/order-list`]);
    } else {
      this.userService
        .getSingleUserByUsername()
        .toPromise()
        .then(dbUser => {
          dbUser !== null && dbUser.requiredPasswordChange
            ? this.router.navigateByUrl(`/secure/change-password`)
            : this.router.navigateByUrl(`/secure/dashboard`);
        });
    }
  }

  private redirectWithNoAccess(): void {
    this.router.navigate(['/account-disabled']);
  }

  private registerKnownActions(): void {
    this.knownActions.push(this.ACTION_CONFIRM);
    this.knownActions.push(this.ACTION_DENY);
    this.knownActions.push(this.ACTION_BID);
    this.knownActions.push(this.ACTION_BID_LIST);
    this.knownActions.push(this.ACTION_CARRIER_CERTIFICATE);
    this.knownActions.push(this.ACTION_ORDER);
    this.knownActions.push(this.ACTION_ORDER_LIST);
  }

  private actionIsKnown(action: string): boolean {
    if (!action) {
      return false;
    }
    return this.knownActions.indexOf(action) !== -1;
  }

  ngAfterViewChecked() {
    this.getFooter().style.position = 'fixed';
  }

  getFooter() {
    return document.querySelector('#app-footer') as HTMLElement;
  }
}
